import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { LogOut, PanelLeft, X, Search, ChevronDown, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import "@/pages/vendor/vendor.css";

/**
 * Shared dashboard shell used by the vendor and admin panels:
 * grouped sidebar with menu search, breadcrumb top bar, user menu.
 *
 * groups: [{ label, items: [{ to, end?, icon, label, tid }] }]
 * Props: outletKey (change it to remount the page, e.g. on store switch), homePath, roleLabel, sidebarFooter (node), topRight (node), menuLinks ([{to,icon,label}]), logoutTestId, userMenuTestId
 */
export default function AppShell({
  groups, homePath, rootLabel = "Dashboard", roleLabel, sidebarFooter, topRight, banner, menuLinks = [], outletKey = "",
  logoutTestId = "logout", userMenuTestId = "user-menu", searchTestId = "menu-search", toggleTestId = "sidebar-toggle",
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => { setMobileOpen(false); }, [pathname]);
  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const doLogout = () => { logout(); navigate("/login"); };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.map((g) => ({ ...g, items: g.items.filter((i) => i.label.toLowerCase().includes(q)) })).filter((g) => g.items.length);
  }, [query, groups]);

  const crumbs = useMemo(() => {
    for (const g of groups) {
      for (const i of g.items) {
        const match = i.end ? pathname === i.to : pathname.startsWith(i.to);
        if (match) return i.end ? [i.label] : [g.label, i.label];
      }
    }
    return [rootLabel];
  }, [pathname, groups, rootLabel]);

  return (
    <div className="vx min-h-screen flex">
      <aside className={`fixed lg:sticky top-0 h-screen z-40 w-[245px] shrink-0 bg-white border-r border-slate-200 flex flex-col transition-all duration-300
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"} ${collapsed ? "lg:-ml-[245px]" : "lg:translate-x-0 lg:ml-0"}`}>
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <Link to={homePath}><img src="/logo-light.png" alt="sell24x7.com" className="h-10 w-auto" /></Link>
          <button className="lg:hidden text-slate-500" onClick={() => setMobileOpen(false)} aria-label="Close menu"><X size={20} /></button>
        </div>
        <div className="px-4 pb-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search menu..." data-testid={searchTestId}
              className="w-full h-9 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-[13px] outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/15 transition" />
          </div>
        </div>
        <nav className="flex-1 px-3 pb-4 overflow-y-auto scrollbar-thin">
          {filtered.map((g) => (
            <div key={g.label} className="mt-3 first:mt-1">
              <div className="px-3 py-1.5 text-[11px] font-semibold text-slate-500">{g.label}</div>
              {g.items.map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.tid}
                  className={({ isActive }) => `vx-nav ${isActive ? "active bg-blue-50 text-blue-600" : "text-neutral-800 hover:bg-slate-50 hover:text-blue-600"} flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] font-medium transition-colors`}>
                  <n.icon size={17} strokeWidth={1.8} /> {n.label}
                </NavLink>
              ))}
            </div>
          ))}
          {filtered.length === 0 && <div className="px-3 py-6 text-sm text-slate-400">No menu matches.</div>}
        </nav>
        {sidebarFooter && <div className="p-3 border-t border-slate-100">{sidebarFooter}</div>}
      </aside>
      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur-md border-b border-slate-200 h-[58px] px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => (window.innerWidth >= 1024 ? setCollapsed((c) => !c) : setMobileOpen(true))} aria-label="Toggle sidebar" data-testid={toggleTestId}
              className="w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:text-blue-600 hover:border-blue-300 transition-colors">
              <PanelLeft size={17} />
            </button>
            <nav className="flex items-center gap-1.5 text-[13px] min-w-0" aria-label="Breadcrumb">
              {crumbs.map((c, i) => (
                <React.Fragment key={c}>
                  {i > 0 && <ChevronRight size={13} className="text-slate-400 shrink-0" />}
                  <span className={i === crumbs.length - 1 ? "font-semibold text-neutral-900 truncate" : "text-slate-500"}>{c}</span>
                </React.Fragment>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {topRight}
            <div className="relative" ref={menuRef}>
              <button onClick={() => setMenuOpen((o) => !o)} data-testid={userMenuTestId} className="flex items-center gap-2.5 pl-1 pr-2 h-10 rounded-lg hover:bg-slate-50 transition-colors">
                <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs font-bold">{user?.name?.[0]?.toUpperCase()}</span>
                <span className="text-left hidden sm:block leading-tight">
                  <span className="block text-[13px] font-bold text-neutral-900">{user?.name}</span>
                  <span className="block text-[11px] text-slate-500">{roleLabel}</span>
                </span>
                <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-1 w-56 rounded-xl bg-white border border-slate-200 shadow-xl py-1.5 z-30">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <div className="text-[13px] font-semibold text-neutral-900 truncate">{user?.name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{user?.email}</div>
                  </div>
                  {menuLinks.map((m) => (
                    <Link key={m.to} to={m.to} onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-[13px] text-neutral-800 hover:bg-slate-50"><m.icon size={15} /> {m.label}</Link>
                  ))}
                  <button onClick={doLogout} data-testid={logoutTestId} className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50"><LogOut size={15} /> Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>
        {banner}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto scrollbar-thin"><div key={`${outletKey}|${pathname}`} className="vx-page"><Outlet /></div></main>
      </div>
    </div>
  );
}
